import { types, getParent } from 'mobx-state-tree';
import type { IFSM } from './fsm';
import type { IObjectType } from './object_type';
import type { ITransition } from './transition';

/**
 * The State instance interface (hand-declared so sibling views see each other).
 */
export interface IState {
  readonly id: string;
  readonly name: string;
  readonly type: 'INITIAL_STATE' | 'STATE' | 'FINAL_STATE';
  asString(): string;
  readonly displayString: string;
  readonly fsm: IFSM;
  readonly objectType: IObjectType;
  readonly transitions: ITransition[];
  readonly incomingTransitions: ITransition[];
  readonly outgoingTransitions: ITransition[];
  readonly selfTransitions: ITransition[];
  setName(name: string): void;
}

/**
 * A MERODE FSM state
 */
const State = types
  .model('State', {
    id: types.identifier,
    name: types.string,
    type: types.enumeration(['INITIAL_STATE', 'STATE', 'FINAL_STATE']),
  })
  .views(self0 => {
    const self = self0 as unknown as IState;
    return {
    // Human-readable label, e.g. for `${state}` interpolation in messages.
    // (MST 7 forbids declaring a view named `toString`, so this is a plain view.)
    asString() {
      return self.displayString;
    },
    // Used in the logger and the report view
    get displayString() {
      const fsmDesc = `${self.objectType.name}.${self.fsm.name}`;
      return `State ${fsmDesc}.${self.name}`;
    },
    // Convenience method to get the FSM
    get fsm(): IFSM {
      return getParent(self0, 2);
    },
    // Convenience method to get the object type
    get objectType(): IObjectType {
      return self.fsm.objectType;
    },
    // get all transitions that have this state as their source or target
    get transitions() {
      return self.fsm.transitions.filter(
        (t: ITransition) => t.sourceState === self || t.targetState === self
      );
    },
    // get all transitions that have this state as their target
    get incomingTransitions() {
      return self.fsm.getIncomingTransitions(self);
    },
    // get all transitions that have this state as their source
    get outgoingTransitions() {
      return self.fsm.getOutgoingTransitions(self);
    },
    // get all transitions that are loops back to this state
    get selfTransitions() {
      return self.fsm.transitions.filter(
        (t: ITransition) => t.sourceState === self && t.targetState === self
      );
    },
    };
  })
  .actions(self => ({
    setName(name: string) {
      self.name = name;
    },
  }));

export default State;
